/**
 * Base URL for Socket.IO (origin + trailing slash). Use with {@link getSocketIoPath}.
 *
 * Priority:
 * 1. REACT_APP_SOCKET_URL — explicit socket server (e.g. https://api.example.com/)
 * 2. REACT_APP_PRIMARY_SERVER_URL — same host as REST API (parsed origin)
 * 3. Same host as the page + /api/ (production SPA + API behind reverse proxy)
 * 4. localhost — REACT_APP_PRIMARY_SERVER_URL if set, else window /api/
 */
export function getSocketBaseUrl() {
    const explicit = process.env.REACT_APP_SOCKET_URL;
    if (explicit && String(explicit).trim()) {
        return String(explicit).trim().replace(/\/?$/, '/');
    }

    const fromEnv = process.env.REACT_APP_PRIMARY_SERVER_URL;
    if (fromEnv && String(fromEnv).trim()) {
        try {
            return `${new URL(fromEnv.trim()).origin}/`;
        } catch {
            console.warn('[socket] invalid REACT_APP_PRIMARY_SERVER_URL', fromEnv);
        }
    }

    let apiBase = `${window.location.protocol}//${window.location.hostname}/api/`;
    if (apiBase.includes('localhost')) {
        const local = process.env.REACT_APP_PRIMARY_SERVER_URL;
        if (local && String(local).trim()) {
            apiBase = local.trim();
        }
    }

    try {
        return `${new URL(apiBase).origin}/`;
    } catch {
        console.warn('[socket] invalid API base, using window.origin', apiBase);
        return `${window.location.origin}/`;
    }
}

/** Default Engine.IO path under the REST API prefix (portal …/api/ and localhost:5000/api/). */
export const DEFAULT_SOCKET_IO_PATH = '/api/socket.io';

/**
 * Socket.IO HTTP path (engine.io). Defaults to {@link DEFAULT_SOCKET_IO_PATH} so URLs are always
 * `origin + /api/socket.io` (e.g. https://portal.zencruz.com/api/socket.io, http://localhost:5000/api/socket.io).
 * Override with REACT_APP_SOCKET_IO_PATH=/socket.io if your Node process only mounts at root.
 */
export function getSocketIoPath() {
    const explicit = process.env.REACT_APP_SOCKET_IO_PATH;
    if (explicit && String(explicit).trim()) {
        const s = String(explicit).trim();
        return s.startsWith('/') ? s : `/${s}`;
    }
    return DEFAULT_SOCKET_IO_PATH;
}

/**
 * Options for socket.io-client. Polling first works through many reverse proxies where
 * WebSocket upgrade fails; upgrade to WS happens after connect when possible.
 */
export function getSocketIoClientOptions() {
    return {
        path: getSocketIoPath(),
        transports: ['polling', 'websocket'],
        timeout: 45000,
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000
    };
}
